import { useSession } from "next-auth/react";
import { useContext, useEffect, useState } from "react";
import { useFirebaseUser } from "utils/useFirebaseUser";
import { TweetContext, useTweetContext } from "context/tweetContext";
import { getNextSlots } from "controllers/schedule";
import { convertToAccountTimezone } from "utils/helpers";
import { getAccount } from "utils/sessionHelper";
import { isMobile } from "react-device-detect";
import { DndProvider } from "react-dnd";
import { TouchBackend } from "react-dnd-touch-backend";
import { HTML5Backend } from "react-dnd-html5-backend";
import { Preview } from "react-dnd-preview";
import theme from "@chakra-ui/theme";
import {
  Grid,
  Text,
  VStack,
  HStack,
  Box,
  Avatar,
  Badge,
  Center,
  Spinner,
  useColorModeValue,
  Tooltip,
  Button,
} from "@chakra-ui/react";
import {
  format,
  startOfMonth,
  eachDayOfInterval,
  endOfMonth,
  startOfWeek,
  endOfWeek,
} from "date-fns";
import { firebaseClient } from "firebaseClient";
import { isTweetFromAccount } from "controllers/schedule";
import { InfoIcon } from "@chakra-ui/icons";
import { useDrag, useDrop } from "react-dnd";
import { ItemTypes } from "components/dnd/itemTypes";
import toast from "react-hot-toast";
import * as Sentry from "@sentry/nextjs";

interface TweetCardProps {
  tweet: {
    id: string;
    status: string;
    profilePic: string;
    text: string;
    message?: string;
    isError?: boolean;
  };
  time: string;
  onDrop?: (tweet: any, slotTime: Date) => void;
}

function DroppableSlot({ time, isAvailable, onClick, onDrop }) {
  const [{ isOver }, drop] = useDrop(() => ({
    accept: ItemTypes.CARD,
    canDrop: () => isAvailable,
    drop: (item: any) => {
      if (onDrop) {
        onDrop(item.tweet, time);
      }
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
    }),
  }));

  return (
    <Button
      ref={drop}
      key={time.getTime()}
      size="sm"
      variant="outline"
      colorScheme="gray"
      onClick={onClick}
      color={useColorModeValue("gray.400", "gray.300")}
      borderColor={useColorModeValue("gray.400", "gray.500")}
      borderStyle="dashed"
      bg={isOver && isAvailable ? useColorModeValue("gray.100", "gray.700") : undefined}
      _hover={{
        bg: useColorModeValue("gray.50", "gray.600")
      }}
    >
      {format(time, 'h:mm a')}
    </Button>
  );
}

function PostCard({ tweet, time }: TweetCardProps) {
  const tweetContext: any = useContext(TweetContext);
  const { data: session } = useSession() ?? {};
  const [{ isDragging }, drag] = useDrag(() => ({
    type: ItemTypes.CARD,
    item: { id: tweet.id, tweet },
    canDrag: () => tweet.status !== "sent",
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }));

  const openTweet = (tweet) => {
    if (tweet?.text) {
      tweetContext.setSelectedTweetPreview(tweet);
      tweetContext.onOpenTweetPreview();
    }
  }

  return (
    <VStack
      ref={drag}
      onClick={() => openTweet(tweet)}
      as="button"
      textAlign="left"
      spacing={2}
      bg={useColorModeValue("white", "gray.700")}
      p={2}
      borderRadius="md"
      opacity={isDragging ? 0.5 : 1}
      cursor={tweet.status === "sent" ? "pointer" : "move"}
    >
      <HStack justify="space-between" w="100%" color={useColorModeValue("#206F97", "blue.200")}>
        <Text fontSize="xs">{time}</Text>
        {tweet.isError && tweet.message && tweet.status !== "sent" ? (
          <Tooltip hasArrow label={"An error occured: " + tweet.message} placement="top">
            <HStack gap={1}>
              <InfoIcon color={useColorModeValue("alert.lightMode.400", "alert.darkMode.400")} w={3} h={3} />
              <Text
                fontSize="xs"
                fontWeight="medium"
                textTransform="capitalize"
                color={useColorModeValue("red.500", "red.300")}
              >
                Error
              </Text>
            </HStack>
          </Tooltip>
        ) : (
          <HStack gap={1}>
              <Text
                fontSize="xs"
                fontWeight="medium"
                textTransform="capitalize"
                color={tweet.status === "sent"
                  ? useColorModeValue("#31C52E", "green.300")
                  : useColorModeValue("#206F97", "blue.200")}
              >
                {tweet.status === "sent" ? "Posted!" : tweet.status}
              </Text>
            </HStack>
        )}
      </HStack>
      <HStack w="100%">
        <Avatar size="xs" src={tweet.profilePic} />
        <Text
          fontSize="sm"
          noOfLines={1}
          color={useColorModeValue(
            "text.lightMode.standard",
            "text.darkMode.standard"
          )}
          fontWeight="medium"
        >
          {tweet.text}
        </Text>
      </HStack>
    </VStack>
  );
}

export function MonthView({ query }: any) {
  const { data: session } = useSession() ?? {};
  const [slots, setSlots] = useState<any[]>([]);
  const [scheduledTweets, setScheduledTweets] = useState<any[]>([]);
  const [publishedTweets, setPublishedTweets] = useState<any[]>([]);
  const [errorTweets, setErrorTweets] = useState<any[]>([]);
  const tweetContext: any = useTweetContext();
  const isAuthenticated = useFirebaseUser();
  const [isLoading, setIsLoading] = useState(true);

  const colorTextGray = useColorModeValue('gray.600', 'gray.300');
  const colorBgMedium = useColorModeValue(
    'background.lightMode.medium',
    'background.darkMode.medium'
  );
  const colorTextLightGray = useColorModeValue('gray.400', 'gray.300');

  const getPublishedTweets = async (monthStart: Date) => {
    if (!isAuthenticated) return;

    const db = firebaseClient.firestore();
    const monthEnd = endOfMonth(monthStart);

    try {
      const query = await db
        .collection("users")
        .doc(getAccount(session).id)
        .collection("tweets")
        .where("status", "==", "sent")
        .where("idAccount", "==", getAccount(session).idAccount)
        .where("dateSent", ">=", monthStart)
        .where("dateSent", "<=", monthEnd)
        .orderBy("dateSent", "desc")
        .get();

      const tweets: any[] = [];
      query.forEach((doc) => {
        const data = doc.data();
        if (isTweetFromAccount(data, session)) {
          tweets.push(data);
        }
      });

      setPublishedTweets(tweets);
    } catch (error) {
      console.error("Error fetching published tweets:", error);
    }
  };

  const getErrorTweets = async (monthStart: Date) => {
    if (!isAuthenticated) return;

    const db = firebaseClient.firestore();
    const monthEnd = endOfMonth(monthStart);

    try {
      const query = await db
        .collection("users")
        .doc(getAccount(session).id)
        .collection("tweets")
        .where("isError", "==", true)
        .where("status", "==", "draft")
        .where("idAccount", "==", getAccount(session).idAccount)
        .where("dateCreated", ">=", monthStart)
        .where("dateCreated", "<=", monthEnd)
        .orderBy("dateCreated", "desc")
        .get();

      const tweets: any[] = [];
      query.forEach((doc) => {
        const data = doc.data();
        if (isTweetFromAccount(data, session)) {
          tweets.push(data);
        }
      });

      setErrorTweets(tweets);
    } catch (error) {
      console.error("Error fetching error tweets:", error);
    }
  };

  const getSlots = async function () {
    try {
      setIsLoading(true);
      const { timezoneDiff } = convertToAccountTimezone(
        new Date(),
        getAccount(session)?.timezone
      );
      const monthStart = startOfMonth(new Date());
      monthStart.setMinutes(monthStart.getMinutes() + timezoneDiff);

      let props = await getNextSlots(session, monthStart, 30);
      const { slots, scheduleTweets } = props;
      setSlots(slots);
      setScheduledTweets(scheduleTweets);

      // Fetch both published and error tweets
      await Promise.all([
        getPublishedTweets(monthStart),
        getErrorTweets(monthStart)
      ]);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      getSlots();
    }
  }, [isAuthenticated, tweetContext.refresh]);

  const monthStart = startOfMonth(new Date());
  const monthEnd = endOfMonth(new Date());
  const firstCalendarDay = startOfWeek(monthStart, { weekStartsOn: 1 });
  const lastCalendarDay = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const allDaysOfMonth = eachDayOfInterval({
    start: firstCalendarDay,
    end: lastCalendarDay,
  });

  const weeks: Date[][] = [];
  for (let i = 0; i < allDaysOfMonth.length; i += 7) {
    weeks.push(allDaysOfMonth.slice(i, i + 7));
  }

  const daysWithSlots = weeks.map((week) => {
    return week.map((date) => {
      const isCurrentMonth = date.getMonth() === monthStart.getMonth();
      let matchingSlot: any = null;

      if (isCurrentMonth) {
        matchingSlot = slots.find(
          (slot) =>
            format(new Date(slot.time), "yyyy-MM-dd") ===
            format(date, "yyyy-MM-dd")
        );
      }

      return {
        date,
        slots: matchingSlot ? matchingSlot.slotsOfTheDay : [],
        isCurrentMonth,
      };
    });
  });

  // Determine which weekday columns to show
  const weekDays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const weekdaysWithSlots = new Set<number>();
  daysWithSlots.forEach(week => {
    week.forEach((dayData, dayIndex) => {
      if (dayData.isCurrentMonth && dayData.slots.length > 0) {
        weekdaysWithSlots.add(dayIndex);
      }
    });
  });

  const filteredHeaders = weekDays.filter((_, index) => weekdaysWithSlots.has(index));
  const filteredWeeks = daysWithSlots.map(week =>
    week.filter((dayData, dayIndex) => weekdaysWithSlots.has(dayIndex))
  );

  const getTweetsForDate = (date: Date) => {
    const scheduledForDate = scheduledTweets.filter((tweet) => {
      const tweetDate = new Date(tweet.scheduledTime.seconds * 1000);
      const { date: dateTimezoned } = convertToAccountTimezone(
        tweetDate,
        getAccount(session)?.timezone
      );
      return format(dateTimezoned, "yyyy-MM-dd") === format(date, "yyyy-MM-dd");
    });

    const publishedForDate = publishedTweets.filter((tweet) => {
      const tweetDate = new Date(tweet.dateSent.toDate());
      const { date: dateTimezoned } = convertToAccountTimezone(
        tweetDate,
        getAccount(session)?.timezone
      );
      return format(dateTimezoned, "yyyy-MM-dd") === format(date, "yyyy-MM-dd");
    });

    const errorForDate = errorTweets.filter((tweet) => {
      const tweetDate = new Date((tweet.scheduledTime || tweet.dateCreated).toDate());
      const { date: dateTimezoned } = convertToAccountTimezone(
        tweetDate,
        getAccount(session)?.timezone
      );
      return format(dateTimezoned, "yyyy-MM-dd") === format(date, "yyyy-MM-dd");
    });

    return [...scheduledForDate, ...publishedForDate, ...errorForDate];
  };

  const isSlotAvailable = (slotTime: Date, tweets: any[]) => {
    // Check if the slot is in the future
    if (slotTime < new Date()) {
      return false;
    }

    // Check if there's already a tweet scheduled for this exact time
    return !tweets.some(tweet => {
      const tweetTime = tweet.scheduledTime
        ? new Date(tweet.scheduledTime.seconds * 1000)
        : new Date(tweet.dateSent.toDate());
      const { date: dateTimezoned } = convertToAccountTimezone(
        tweetTime,
        getAccount(session)?.timezone
      );
      return format(dateTimezoned, 'HH:mm') === format(slotTime, 'HH:mm');
    });
  };

  const handleSlotClick = (time: Date) => {
    let newTime = new Date(time);
    tweetContext.open();
    tweetContext.setNextSlot(newTime);
    let textState =
      tweetContext?.textState ??
      tweetContext?.refComposer?.current?.textState();
    tweetContext.setTextState({
      ...textState,
      scheduledTime: newTime,
    });
  };

  const handleDrop = async (tweet: any, slotTime: Date) => {
    try {
      const { timezoneDiff } = convertToAccountTimezone(
        new Date(),
        getAccount(session)?.timezone
      );

      // Update local state first
      const newScheduledTime = new Date(slotTime.getTime() + 1000 * 60 * timezoneDiff);

      // Update the scheduledTweets array
      setScheduledTweets(prev => {
        return prev.map(t => {
          if (t.id === tweet.id) {
            return {
              ...t,
              scheduledTime: {
                seconds: Math.floor(newScheduledTime.getTime() / 1000),
                nanoseconds: 0
              }
            };
          }
          return t;
        });
      });

      // Update backend
      const db = firebaseClient.firestore();
      await db
        .collection("users")
        .doc(getAccount(session).id)
        .collection("tweets")
        .doc(tweet.id)
        .update({
          scheduledTime: newScheduledTime,
        })
        .catch((e) => {
          // Revert local state on error
          setScheduledTweets(prev => {
            return prev.map(t => {
              if (t.id === tweet.id) {
                return tweet; // Revert to original tweet
              }
              return t;
            });
          });
          toast.error("Error during scheduling change");
          console.log("Error during scheduling change: " + e.message);
          Sentry.captureException(e);
          throw e;
        });

      toast.success("Post rescheduled successfully");
    } catch (e) {
      console.error(e);
      Sentry.captureException(e);
      toast.error("Failed to reschedule post");
    }
  };

  if (isLoading) {
    return (
      <Center h="400px">
        <Spinner size="xl" color="blue.500" />
      </Center>
    );
  }

  const content = (
    <Box p={4}>
      <Grid
        templateColumns={`repeat(${filteredHeaders.length}, minmax(0, 1fr))`}
        gap={2}
      >
        {/* Filtered week headers */}
        {filteredHeaders.map((day) => (
          <Box key={day} textAlign="center" py={2}>
            <Text fontSize="sm" color={colorTextGray}>
              {day}
            </Text>
          </Box>
        ))}

        {/* Filtered calendar grid */}
        {filteredWeeks.map((week, weekIndex) =>
          week.map((dayData, dayIndex) => {
            const tweets = getTweetsForDate(dayData.date);

            // Create combined array of tweets and available slots
            const timeSlots = dayData.isCurrentMonth ? dayData.slots.map((slot: any) => {
              const slotTime = new Date(slot.time);
              return {
                type: 'slot',
                time: slotTime,
                id: slot.id,
                isAvailable: isSlotAvailable(slotTime, tweets)
              };
            }) : [];

            const tweetSlots = tweets.map(tweet => {
              const tweetDate = tweet.scheduledTime
                ? new Date(tweet.scheduledTime.seconds * 1000)
                : new Date(tweet.dateSent.toDate());
              const { date: dateTimezoned } = convertToAccountTimezone(
                tweetDate,
                getAccount(session)?.timezone
              );
              return ({
                type: 'tweet',
                time: new Date(dateTimezoned),
                tweet
              })
            });

            // Combine and sort all items chronologically
            const sortedItems = [...timeSlots, ...tweetSlots].sort((a, b) =>
              a.time.getTime() - b.time.getTime()
            );

            return (
              <Box
                key={format(dayData.date, "yyyy-MM-dd")}
                p={3}
                bg={colorBgMedium}
                borderRadius="md"
                minH="180px"
                opacity={dayData.isCurrentMonth ? 1 : 0.5}
              >
                <VStack align="stretch" spacing={2}>
                  <HStack justify="space-between">
                    <Text
                      fontSize="xs"
                      fontWeight="bold"
                      color={colorTextLightGray}
                    >
                      {format(dayData.date, "dd MMM")}
                    </Text>
                  </HStack>

                  {/* Render sorted items */}
                  {dayData.isCurrentMonth && sortedItems.map((item) => {
                    if (item.type === 'slot' && item.isAvailable) {
                      return (
                        <DroppableSlot
                          key={item.id}
                          time={item.time}
                          isAvailable={item.isAvailable}
                          onClick={() => handleSlotClick(item.time)}
                          onDrop={handleDrop}
                        />
                      );
                    } else if (item.type === 'tweet') {
                      return (
                        <PostCard
                          key={item.tweet.id}
                          tweet={item.tweet}
                          time={format(item.time, "hh:mm a")}
                        />
                      );
                    }
                    return null;
                  })}
                </VStack>
              </Box>
            )
          })
        )}
      </Grid>
    </Box>
  );

  if (isMobile) {
    return (
      <DndProvider backend={TouchBackend}>
        {content}
        <Preview generator={generatePreview} />
      </DndProvider>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      {content}
    </DndProvider>
  );
}

function generatePreview({ item, style }) {
  style.backgroundColor = theme.colors.gray[100];
  style.borderRadius = "12px";
  style.height = "40px";
  style.width = "200px";
  style.display = "flex";
  style.padding = "10px";
  style.paddingLeft = "20px";
  style.alignItems = "center";
  style.overflow = "hidden";
  style.whiteSpace = "nowrap";
  return <div style={style}>{item?.tweet?.text}</div>;
}